import { useEffect } from 'react';

import { useSciDomain } from 'components';
import {
  CLARITY_START_DELAY_MS,
  CLARITY_TABS_CHANNEL_NAME,
  FINAL_ORDER_STATUSES,
} from 'constants/clarity.constants';
import { OrderStatus } from 'enums';
import { clarityUtils } from 'utils';

type UseClarityRecordingOptions = {
  orderId?: string;
  status?: OrderStatus;
};

type TabMessage = { type: 'ping' | 'pong'; orderId: string };

/**
 * Starts the Clarity recording for an order page.
 *
 * The start is delayed because some orders redirect away almost immediately (deeplink flows) —
 * recording those adds a stream of empty sessions. Unmounting before the delay elapses cancels the
 * start, so a page that redirects never begins recording.
 *
 * The same delay doubles as a window for discovering other open order tabs: they share Clarity's
 * session cookie, so it may only be reset when this tab is the only one recording.
 */
export const useClarityRecording = ({
  orderId,
  status,
}: UseClarityRecordingOptions) => {
  const queryResultSciDomain = useSciDomain();
  const projectId = queryResultSciDomain?.data?.mcProjectId;

  useEffect(() => {
    if (
      !projectId ||
      !orderId ||
      !status ||
      FINAL_ORDER_STATUSES.includes(status)
    ) {
      return;
    }

    // Absent in older browsers — then the session cookie is left alone rather than risking another
    // tab's recording.
    const channel =
      typeof BroadcastChannel === 'undefined'
        ? undefined
        : new BroadcastChannel(CLARITY_TABS_CHANNEL_NAME);
    let hasOtherOrderTab = false;

    if (channel) {
      channel.onmessage = ({ data }: MessageEvent<TabMessage>) => {
        if (data.orderId === orderId) {
          return;
        }
        if (data.type === 'ping') {
          channel.postMessage({ type: 'pong', orderId } satisfies TabMessage);
        } else {
          hasOtherOrderTab = true;
        }
      };
      channel.postMessage({ type: 'ping', orderId } satisfies TabMessage);
    }

    const timeout = setTimeout(
      () =>
        clarityUtils.initialize(projectId, orderId, {
          canStartNewSession: !!channel && !hasOtherOrderTab,
        }),
      CLARITY_START_DELAY_MS,
    );

    return () => {
      clearTimeout(timeout);
      channel?.close();
    };
  }, [projectId, orderId, status]);
};
